import { FormField, isSome } from '@crocodille/common';
import cn from 'classnames';
import React, { ForwardedRef, forwardRef, useMemo, useRef } from 'react';

import styles from './input.module.scss';

const CrossIcon = (
  <svg
    className={styles.clearIcon}
    fill="none"
    width="16px"
    height="16px"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

export type InputProps = Omit<
  React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >,
  'value' | 'onChange'
> & {
  readonly children?: React.ReactElement<
    React.PropsWithChildren<Record<string, unknown>>,
    typeof Icon
  >;
  readonly showClearButton?: boolean;
  readonly value?: string;
  readonly onChange?: (value: string) => void;
  readonly invalid?: boolean;
  readonly innerClassName?: string;
};

const InputElement = forwardRef(
  (
    {
      className,
      children,
      showClearButton = false,
      value,
      onChange,
      size = 1,
      invalid = false,
      innerClassName,
      ...props
    }: InputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const clearButtonShown = typeof value === 'string' && value.length > 0;

    const clearButtonRef = useRef<HTMLButtonElement>(null);

    const handleOnChange = useMemo(
      () =>
        onChange
          ? (e: React.ChangeEvent<HTMLInputElement>) =>
              void onChange?.(e.target.value)
          : undefined,
      [onChange],
    );

    return (
      <div
        className={cn(className, styles.container, {
          [styles.withClearButton]: showClearButton,
          [styles.isInvalid]: invalid,
        })}
      >
        {children}
        <input
          ref={ref}
          className={cn(styles.input, innerClassName)}
          value={value}
          onChange={handleOnChange}
          size={size}
          {...props}
        />
        {showClearButton ? (
          <button
            ref={clearButtonRef}
            type="button"
            tabIndex={clearButtonShown ? 0 : -1}
            aria-hidden={!clearButtonShown}
            className={cn(styles.clearButton, {
              [styles.isShown]: clearButtonShown,
            })}
            onClick={() => onChange?.('')}
          >
            {CrossIcon}
          </button>
        ) : null}
      </div>
    );
  },
);

InputElement.displayName = 'Input';

const Icon: React.FC<React.PropsWithChildren<Record<string, unknown>>> = ({
  children,
}) => {
  return (
    <span role="presentation" className={styles.icon}>
      {children}
    </span>
  );
};

const Input = Object.assign(InputElement, { Icon });

export function fieldToInputProps({
  value,
  onChange,
  onBlur,
  required,
  disabled,
  error,
}: FormField<string>): Pick<
  InputProps,
  'value' | 'onChange' | 'onBlur' | 'required' | 'disabled' | 'invalid'
> {
  return {
    value,
    onChange,
    onBlur,
    required,
    disabled,
    invalid: isSome(error),
  };
}

export default Input;
